import { Link } from "react-router-dom";
import { ArrowRight, MessageCircle } from "lucide-react";
import { useI18n } from "../i18n/LanguageProvider";
import { WHATSAPP_LINK, SITE } from "../config/site";
import { Reveal } from "./motion/Reveal";
import Breadcrumbs from "./Breadcrumbs";

export function SectionHeading({ eyebrow, title, sub, align = "left", light = false }) {
  const alignCls = align === "center" ? "text-center mx-auto" : "text-left";
  return (
    <div className={`max-w-3xl ${alignCls}`}>
      {eyebrow && <p className={`label-eyebrow ${light ? "!text-turquoise-light" : ""}`}>{eyebrow}</p>}
      <h2 className={`mt-3 text-3xl sm:text-4xl lg:text-[2.75rem] leading-[1.1] ${light ? "text-white" : "text-navy"}`}>
        {title}
      </h2>
      {sub && (
        <p className={`mt-5 text-base sm:text-lg leading-relaxed ${light ? "text-white/70" : "text-navy/70"}`}>
          {sub}
        </p>
      )}
    </div>
  );
}

// Reusable conversion band used across pages.
export function CTABand() {
  const { t, p } = useI18n();
  return (
    <section className="container-dv py-16 lg:py-24" data-testid="cta-band">
      <Reveal>
        <div className="relative overflow-hidden rounded-[2rem] bg-navy px-6 py-14 sm:px-14 sm:py-16 grain">
          <div
            className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full opacity-40 blur-3xl"
            style={{ background: "radial-gradient(circle,#0D9488,transparent 70%)" }}
          />
          <div className="relative max-w-2xl">
            <p className="label-eyebrow !text-turquoise-light">{SITE.brand}</p>
            <h2 className="mt-3 text-3xl sm:text-4xl text-white leading-tight">{t.ctaBand.title}</h2>
            <p className="mt-4 text-lg text-white/70">{t.ctaBand.sub}</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link
                to={p("contact")}
                data-testid="ctaband-plan-btn"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-7 py-4 text-base font-semibold text-navy hover:bg-turquoise hover:text-white transition-colors"
              >
                {t.cta.myFreePlan}
                <ArrowRight className="h-4 w-4" />
              </Link>
              <a
                href={WHATSAPP_LINK()}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="ctaband-whatsapp-btn"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-white/25 px-7 py-4 text-base font-semibold text-white hover:bg-white/10 transition-colors"
              >
                <MessageCircle className="h-4 w-4" />
                {t.cta.whatsapp}
              </a>
            </div>
          </div>
        </div>
      </Reveal>
    </section>
  );
}


// Reusable page hero for interior pages (breadcrumbs + eyebrow + title + intro).
export function PageHero({ eyebrow, title, intro, crumbs = [], image }) {
  return (
    <section className="relative overflow-hidden bg-mist" data-testid="page-hero">
      <div
        className="pointer-events-none absolute -right-32 -top-24 h-96 w-96 rounded-full opacity-25 blur-3xl"
        style={{ background: "radial-gradient(circle,#14B8A6,transparent 70%)" }}
      />
      <div className="container-dv relative py-12 lg:py-16">
        <Breadcrumbs crumbs={crumbs} />
        <div className={`mt-6 grid gap-8 ${image ? "lg:grid-cols-12" : ""}`}>
          <div className={image ? "lg:col-span-7 flex flex-col justify-center" : "max-w-3xl"}>
            {eyebrow && <p className="label-eyebrow">{eyebrow}</p>}
            <h1 className="mt-3 font-serif text-4xl leading-[1.08] tracking-tight text-navy sm:text-5xl lg:text-[3.25rem]">
              {title}
            </h1>
            {intro && <p className="mt-5 max-w-2xl text-lg leading-relaxed text-navy/70">{intro}</p>}
          </div>
          {image && (
            <div className="lg:col-span-5">
              <div className="overflow-hidden rounded-[1.75rem] shadow-lift">
                <img src={image.src} alt={image.alt} className="h-64 w-full object-cover sm:h-80 lg:h-full" loading="eager" />
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

// Renders a list of body paragraphs into styled prose.
export function Prose({ paragraphs = [] }) {
  return (
    <div className="prose-dv">
      {paragraphs.map((para, i) => (
        <p key={i}>{para}</p>
      ))}
    </div>
  );
}
